import React from 'react';
import { Calendar, Paperclip, MessageCircle, AlertCircle, CheckSquare } from 'lucide-react';
import { format, isPast, differenceInHours } from 'date-fns';
import UserAvatar from './UserAvatar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { mentionMarkdownComponents } from './Mentions';
import { TagChipRow } from './TagChip';
import { subtaskStatus } from './SubtaskList';
import './TaskCard.css';
import BstId from './BstId';

// Tags per card before the rest collapse into "+N" (WR-010). A task may
// carry any number; a card that grows with them wrecks the column rhythm.
const MAX_CARD_TAGS = 3;

function TaskCard({ task, tags = [], isDragging, horizonWeeks, horizonTitle }) {
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'var(--danger)';
      case 'medium': return 'var(--accent)';
      case 'low': return 'var(--status-todo)';
      default: return 'var(--gray-400)';
    }
  };

  const now = new Date();
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const isDone = task.status === 'done';
  const isOverdue = dueDate && isPast(dueDate) && !isDone;
  const isDueSoon = dueDate && !isPast(dueDate) && differenceInHours(dueDate, now) <= 24 && !isDone;

  // Subtask progress (WR-011). Only 'done' counts — an in-progress subtask
  // is work still open, and counting it would overstate the card.
  const subtasks = task.subtasks || [];
  const subtasksDone = subtasks.filter(st => subtaskStatus(st) === 'done').length;

  return (
    <div className={`task-card ${isDragging ? 'dragging' : ''}`}>
      <div className="task-card-header">
        {/* Task id, top corner (WR-013 §4). */}
        <BstId id={task.bstId} className="task-card-bst" />
        {/* WR-024 B4: this task's Horizon window, when it is on one. */}
        {horizonWeeks && <span className="task-card-horizon" title={horizonTitle}>{horizonWeeks}</span>}
        <div className="task-priority-badge">
          <div
            className="priority-indicator"
            style={{ background: getPriorityColor(task.priority) }}
          />
          <span className={`badge badge-priority-${task.priority}`}>
            {task.priority}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span className={`badge badge-${task.status}`}>
          {task.status === 'todo' ? 'To Do' : task.status === 'inprogress' ? 'In Progress' : 'Done'}
        </span>
      </div>
      <h4 className="task-title">{task.title}</h4>

      {tags.length > 0 && (
        <TagChipRow tags={tags} max={MAX_CARD_TAGS} className="task-card-tags" />
      )}

      {task.description && (
        <div className="task-description task-card-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionMarkdownComponents}>
            {task.description.length > 120 ? task.description.slice(0, 120) + '...' : task.description}
          </ReactMarkdown>
        </div>
      )}

      <div className="task-footer">
        <div className="task-meta">
          {task.dueDate && (
            <div className={`task-due-date ${isOverdue ? 'overdue' : isDueSoon ? 'due-soon' : ''}`}>
              <Calendar size={14} />
              <span>{format(new Date(task.dueDate), 'MMM d')}</span>
              {isOverdue && <AlertCircle size={14} />}
              {isDueSoon && <span className="due-soon-badge">Soon</span>}
            </div>
          )}

          {subtasks.length > 0 && (
            <div className="task-subtask-progress" title={`${subtasksDone} of ${subtasks.length} subtasks done`}>
              <CheckSquare size={14} />
              <span>{subtasksDone}/{subtasks.length}</span>
            </div>
          )}

          {task.attachments && task.attachments.length > 0 && (
            <div className="task-attachments">
              <Paperclip size={14} />
              <span>{task.attachments.length}</span>
            </div>
          )}
        </div>

        {task.assignedTo && task.assignedTo.length > 0 && (
          <div className="task-assignees">
            {task.assignedTo.slice(0, 3).map((user, idx) => (
              <UserAvatar
                key={user._id}
                user={user}
                className="avatar avatar-xs"
                title={user.name}
                style={{
                  width: '24px',
                  height: '24px',
                  fontSize: '10px',
                  marginLeft: idx > 0 ? '-8px' : '0',
                  zIndex: task.assignedTo.length - idx
                }}
              />
            ))}
            {task.assignedTo.length > 3 && (
              <div
                className="avatar avatar-xs"
                style={{
                  width: '24px',
                  height: '24px',
                  fontSize: '10px',
                  marginLeft: '-8px',
                  background: 'var(--gray-400)'
                }}
              >
                +{task.assignedTo.length - 3}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TaskCard;
